import axios, { AxiosResponse } from "axios";
import { ArrayFromEnvelope, configService, ObjectFromEnvelope } from "./config.service";
import { IPoolPost, IPoolPut, Pool } from "../models/pools";
import { convertDateOnlyToUtc } from "../utils/dateUtils";
import { WorkHourPoolStatus } from "../models/workHourPoolStatus";
import { User } from "@/modules/security/models/user-model";
import store from "@/store";

class PoolService {
  async fetchPools(): Promise<Pool[]> {
    const user: User = store.getters["security/getUser"]();
    let url = "";
    if (user.isManager()) {
      url = configService.APIInstitutionUrl(
        "work_hour_pools.json?institution_id=" + configService.institutionId()
      );
    } else {
      const employee = store.getters["employees/GetEmployeeById"](user.employee_id);
      const employmentIds = employee.allEmployments().map((e) => e.id);
      url = configService.personaleOrAdminUrl(
        "work_hour_pools.json?employment_ids=" + employmentIds.toString()
      );
    }
    return new Promise((resolve, reject) => {
      axios
        .get<Pool[]>(url)
        .then((response: AxiosResponse) => {
          resolve(Pool.fromJSONArray(ArrayFromEnvelope<Pool>(response.data)));
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
  async fetchPoolsByEmploymentId(employmentId: number): Promise<Pool[]> {
    return new Promise((resolve, reject) => {
      axios
        .get<Pool[]>(
          configService.APIInstitutionUrl("work_hour_pools.json?employment_id=" + employmentId)
        )
        .then((response: AxiosResponse) => {
          resolve(Pool.fromJSONArray(ArrayFromEnvelope<Pool>(response.data)));
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
  async createPool(pool: Pool): Promise<Pool> {
    return new Promise((resolve, reject) => {
      axios
        .post<Pool>(
          configService.APIInstitutionUrl("work_hour_pools.json"),
          pool.convertToPostObject()
        )
        .then((response: AxiosResponse) => {
          resolve(Pool.fromJSON(ObjectFromEnvelope<Pool>(response.data)));
        })
        .catch((error) => reject(error));
    });
  }
  async updatePool(pool: Pool): Promise<Pool> {
    return new Promise((resolve, reject) => {
      axios
        .put<Pool>(
          configService.APIInstitutionUrl("work_hour_pools/" + pool.id + ".json"),
          pool.convertToPutObject()
        )
        .then((response: AxiosResponse) => {
          resolve(Pool.fromJSON(ObjectFromEnvelope<Pool>(response.data)));
        })
        .catch((error) => reject(error));
    });
  }
  async createUpdatePools(obj: {
    pools_to_create: IPoolPost[];
    pools_to_update: IPoolPut[];
  }): Promise<{ createdPools: Pool[]; updatedPools: Pool[]; errors: string[] }> {
    return new Promise((resolve, reject) => {
      axios
        .post<Pool>(
          configService.APIInstitutionUrl("work_hour_pools/create_update_pools.json"),
          obj
        )
        .then((response: AxiosResponse) => {
          const createdPools: Pool[] = Pool.fromJSONArray(
            response.data.data.created_work_hour_pools
          );
          const updatedPools: Pool[] = Pool.fromJSONArray(
            response.data.data.updated_work_hour_pools
          );
          const errors = response.data.data.errors;
          resolve({ createdPools, updatedPools, errors });
        })
        .catch((error) => reject(error));
    });
  }
  async deletePool(poolId: number): Promise<any> {
    return new Promise((resolve, reject) => {
      axios
        .delete<Pool>(configService.APIInstitutionUrl("work_hour_pools/" + poolId + ".json"))
        .then((response: AxiosResponse) => {
          resolve(response.data);
        })
        .catch((error) => reject(error));
    });
  }
  async getPoolStatus(date: Date): Promise<WorkHourPoolStatus> {
    const url = configService.personaleOrAdminUrl(
      "work_hour_pool_status.json?date=" + convertDateOnlyToUtc(date)
    );
    return new Promise((resolve, reject) => {
      axios
        .get<WorkHourPoolStatus>(url)
        .then((response: AxiosResponse) => {
          resolve(
            WorkHourPoolStatus.fromJSON(ObjectFromEnvelope<WorkHourPoolStatus>(response.data))
          );
        })
        .catch((error) => reject(error));
    });
  }

  async getPoolStatusByID(poolId: number): Promise<WorkHourPoolStatus> {
    return new Promise((resolve, reject) => {
      axios
        .get<WorkHourPoolStatus>(
          configService.APIInstitutionUrl("work_hour_pool_status/" + poolId.toString() + ".json")
        )
        .then((response: AxiosResponse) => {
          resolve(
            WorkHourPoolStatus.fromJSON(ObjectFromEnvelope<WorkHourPoolStatus>(response.data))
          );
        })
        .catch((error) => reject(error));
    });
  }
}

export const poolService: PoolService = new PoolService();
